

/**
 * Parses a URL by determining if it's an external URL or a relative URL.
 *
 * @param {string} url - The URL to parse, which can be either relative or absolute
 * @returns {string} The parsed URL as an absolute URL
 */
function parseUrl(url) {
  if (url.startsWith("http") || url.startsWith("www") || url.includes(".com")) {
    return url;
  } else {
    return `${window.location.protocol}//${window.location.host}/${url.replace(
      /^\//,
      ""
    )}`;
  }
}

/**
 * Process the items recursively to create a new array of objects.
 *
 * @param {Array} items - The items to process.
 * @returns {Array} The processed items.
 */
function processItems(items, useCategory = false) {
  if (!items) return [];

  return Object.values(items).map((item) => {
    if (item.presetGroup) {
      item.itemName = formatNavItemName(item.presetGroup);
    }

    return {
      id: item.itemId,
      name: item.itemName ?? "",
      url: item.itemUrl ? parseUrl(item.itemUrl) : "",
      type: item.type,
      className: getItemClassName(item),
      items: processItems(item.items), // Recursive call to process subitems
      hasSubItems: item.items.length > 0 && !item.inactiveSubnav,
      mediaFile: getWebpImageUrl(item.mediaFile),
      customMediaFile: item.customMediaFile,
      useImage: item.useImage,
      usePlaceholderImage: item.usePlaceholderImage,
      placeholderUrl: item.placeholderUrl,
      parentId: item.parentId,
      navBarId: item.navBarId,
      categoryId: useCategory ? item.catalogCategoryId : null,
      brandId: item.catalogBrandId
    };
  });
}

/**
 * Convert image URLs to webp format and update dimensions
 *
 * @param {string} url
 * @return {string|null} The webp image URL or null if the input URL is invalid
 */
function getWebpImageUrl(url) {
  if (!url) return null;

  // Then replace the extension if it's jpg/jpeg
  if (/\.(jpe?g)$/i.test(url)) {
    url = url.replace(/\.(jpe?g)$/i, ".webp");
  }

  return url;
}

/**
 * Format the navigation item name by splitting it into words and capitalizing each word.
 *
 * @param {string} str - The string to format.
 * @returns {string} The formatted string.
 */
function formatNavItemName(str) {
  // Split by capital letters and join with space
  let words = str.split(/(?=[A-Z])/).join(" ");
  // Capitalize first letter of each word
  return words
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

/**
 * Decodes HTML entities in a string using a temporary textarea element.
 *
 * @param {string} encodedString - The string with HTML entities to decode.
 * @returns {string} The decoded string.
 */
function decodeHtmlEntities(encodedString) {
  const textarea = document.createElement("textarea");
  textarea.innerHTML = encodedString;
  return textarea.value;
}

/**
 * Parses the navigation items JSON string and returns an array of objects.
 * Each object represents a navigation item and contains its properties.
 * The items are sorted by their order property.
 *
 * @param {string} navItemsJson - The JSON string of the navigation items.
 * @returns {Array} The array of navigation items.
 */
function parseNavItems(navItemsJson) {
  const decodedJson = decodeHtmlEntities(navItemsJson);
  const navItems = JSON.parse(decodedJson);
  const navItemsArray = Object.values(navItems);
  navItemsArray.sort((a, b) => parseInt(a.itemOrder) - parseInt(b.itemOrder));

  return navItemsArray;
}

/**
 * Builds the navigation hierarchy based on parentIds and sets child items recursively.
 *
 * @param {Array} navItemsArray - The array of navigation items.
 * @returns {Array} The array of navigation items with hierarchy.
 */
function getHierarchy(navItemsArray) {
  // Group items by parentId for faster lookup
  const itemsByParentId = navItemsArray.reduce((acc, item) => {
    acc[item.parentId] = acc[item.parentId] || [];
    acc[item.parentId].push(item);
    return acc;
  }, {});

  // Build the hierarchy recursively, including sub-items and group items
  const buildHierarchy = (parentId) => {
    return (itemsByParentId[parentId] || []).map((item) => {
      // IRecursively build its sub-items
      item.items = buildHierarchy(item.itemId);
      return item;
    });
  };

  return buildHierarchy(null);
}

/**
 * Checks if the navigation item has a subnav and returns the subnav items.
 *
 * @param {Object} item - The navigation item.
 * @returns {Array} The subnav items.
 */
function getSubItems(item, useCategory = false) {
  if (item.subnav) {
    const subItems = getHierarchy(item.subnav);
    return processItems(subItems, useCategory);
  }

  return processItems(item.items, useCategory);
}

/**
 * Helper function to get the class name for the navigation item.
 *
 * @param {Object} item - The navigation item.
 * @returns {string} The class name for the navigation item.
 */
function getItemClassName(item) {
  let className = "";
  if(item.itemName) {
    className = "nav-" + item.itemName.toLowerCase()
      .replace(/[^a-z0-9]/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "")
  }

  return className;
}

/**
 * Sets the navigation items by parsing the JSON string and building the hierarchy.
 *
 * @param {string} navItemsJson - The JSON string of the navigation items.
 * @param {boolean} isParsed - Indicates if the JSON string is already parsed.
 * @return {Array} The array of navigation items.
 */
function setNavItems(navItemsJson, isParsed = false, useCategory = false) {
  try {
    // Build the navigation hierarchy
    let navItemsArray = isParsed ? navItemsJson : parseNavItems(navItemsJson);
    const hierarchy = getHierarchy(navItemsArray);

    const navItems = Object.values(hierarchy).map((item) => ({
      id: item.itemId,
      name: item.itemName ?? "",
      url: item.itemUrl ? parseUrl(item.itemUrl) : "",
      type: item.type,
      className: getItemClassName(item),
      items: getSubItems(item, useCategory),
      hasSubItems: (item.items.length > 0 || item.subnav) && !item.inactiveSubnav,
      mediaFile: getWebpImageUrl(item.mediaFile),
      customMediaFile: item.customMediaFile,
      useImage: item.useImage,
      usePlaceholderImage: item.usePlaceholderImage,
      placeholderUrl: item.placeholderUrl,
      parentId: item.parentId,
      navBarId: item.navBarId,
      categoryParent: item.subnav ? true : false,
      categoryId: useCategory ? item.catalogCategoryId : null,
      brandId: item.catalogBrandId
    }));

    return navItems;
  } catch (error) {
    console.error("Error parsing nav items JSON", error);
    return [];
  }

}

/**
 * Processes navigation bars from JSON and normalizes their item structure.
 * Ensures items are always processed as arrays even if provided as object literals.
 *
 * @param {string} navbarsJson - JSON string of navigation bars
 * @returns {Array} Processed navigation bars with normalized item arrays
 */
function getAdditionalNavBars(navbarsJson) {
  try {
    const navbars = parseNavItems(navbarsJson);

    return navbars.map((navbar) => {
      // Convert object of objects to array of objects
      const navbarItems = navbar.items
        ? Array.isArray(navbar.items)
          ? navbar.items
          : Object.values(navbar.items)
        : [];

      return {
        id: navbar.id,
        items: navbarItems.length > 0 ? setNavItems(navbarItems, true) : [],
      };
    });
  } catch (error) {
    console.error("Error parsing additional navbars JSON", error);
    return [];
  }
}

/**
 * Base class for managing navigation items and the staet and behavior of the navigation
 *
 * @class NavigationManager
 * @param {Array} navItems - The navigation items.
 * @param {Array} secondaryNavItems - The secondary navigation items.
 * @param {Array} additionalNavbars - The additional navigation items.
 * @param {number} maxMobileLevels - The maximum number of mobile levels.
 * @param {boolean} hideMobileSubHeaders - Whether to hide mobile subheaders.
 * @param {string} mobileFooterNav - The mobile footer navigation ID.
 * @param {Array} showGroups - The groups to show in the navigation regardless of subitems.
 * @param {boolean} showNavImagesOnMobile - Whether to show navigation images on mobile.
 * @param {Array} allowMobileSubHeadersList - List of items that are allowed to show subheaders on mobile.
 * @param {boolean} showSecondaryNavOnMobile - Whether to show secondary navigation on mobile.
 * @param {boolean} useSecondaryNavInMobileFooter - Whether to use secondary navigation in the mobile footer.
 * @returns {NavigationManager} The navigation manager instance.
 *
 */
class NavigationManager {
  constructor(
    navItems,
    secondaryNavItems,
    additionalNavbars,
    maxMobileLevels,
    hideMobileSubHeaders,
    mobileFooterNav,
    showGroups,
    showNavImagesOnMobile,
    allowMobileSubHeadersList = [],
    showSecondaryNavOnMobile,
    useSecondaryNavInMobileFooter
  ) {
    this.navItems = navItems;
    this.secondaryNavItems = secondaryNavItems;
    this.additionalNavbars = additionalNavbars;
    this.mobileNavStack = [];
    this.parentItem = null;
    this.childItems = [];
    this.mobileFooterItems = [];
    this.mobileLevels = maxMobileLevels;
    this.maxLevel = false;
    this.showDropdown = false;
    this.currentNavItem = null;
    this.open = false;
    this.hideMobileSubHeaders = hideMobileSubHeaders;
    this.mobileFooterNav = mobileFooterNav;
    this.showGroups = showGroups;
    this.hasNavImages = false;
    this.navImages = [];
    this.showNavImagesOnMobile = showNavImagesOnMobile;
    this.allowMobileSubHeadersList = allowMobileSubHeadersList;
    this.showSecondaryNavOnMobile = showSecondaryNavOnMobile;
    this.useSecondaryNavInMobileFooter = useSecondaryNavInMobileFooter;
  }

  init() {
    this.updateTabIndex();

    window.addEventListener("resize", () => {
      this.handleResize();
    });

    // Set the mobile nav items
    this.setMobileNavItems();

    // Set the mobile footer items
    this.setMobileFooterItems();
  }

  // Abstract method to be implemented by the child classes
  getCategoryNavItems() {
    return this.navItems;
  }

  // Abstract method to be implemented by the child classes
  setMobileNavItems() {
    this.mobileNavItems = this.navItems;
  }
  // Set the mobile footer items based on the mobile footer nav ID
  setMobileFooterItems() {
    this.mobileFooterItems = [];

    // Add secondary nav items to mobile footer items if configured
    if(this.useSecondaryNavInMobileFooter && this.secondaryNavItems.length > 0) {
      this.mobileFooterItems = [...this.secondaryNavItems.flatMap((item) =>
        item.hasSubItems ? item.items : [item]
      )];
    }

    // Check if mobileFooterNav is set and find the corresponding navbar
    if(this.mobileFooterNav) {
      const navbar = this.additionalNavbars.find(
        (bar) => bar.id == this.mobileFooterNav
      );

      if (navbar && navbar.items.length > 0) {
        const additionalItems = navbar.items.flatMap((item) =>
          item.hasSubItems ? item.items : [item]
        );

        // Add the additional items to the mobile footer items
        this.mobileFooterItems = [...this.mobileFooterItems, ...additionalItems];
      }
    }
  }

  // Update the tabindex of the focusable elements in the mobile menu
  updateTabIndex() {
    const focusableElements = document.querySelectorAll(
      ".mobile-menu a, .mobile-menu button"
    );

    focusableElements.forEach((element) => {
      element.setAttribute("tabindex", this.open ? 0 : -1);
    });
  }

  // Handle the resize event to toggle mobile navigation
  handleResize() {
    if (window.innerWidth >= 1100 && this.open) {
      this.toggleMobileNavigation();
    }
  }

  // Get the nav items to be displayed in the mobile navigation
  getMobileNavItems() {
    if (this.childItems.length > 0) {
      return this.childItems.filter((item) => item.type !== "image");
    }

    return this.mobileNavItems.filter((item) => item.type !== "image");
  }

  // Set current nav item to null
  resetNavItem() {
    this.currentNavItem = null;
  }

  // Set the current nav item to the specified item
  setNavItem(navItem) {
    this.currentNavItem = navItem;
  }

  // Returns whether to use the url as a link or not for the given item
  useLink(item) {
    if (item.url && !item.hasSubItems) {
      return true;
    } else if (item.url && item.hasSubItems && this.maxLevel) {
      return true;
    } else {
      return false;
    }
  }

  // Update the mobile navigation stack and set the child items based on the selected nav item
  updateMobileNav(navItem) {
    // Check if maxLevel will be reached
    if (this.mobileNavStack.length < this.mobileLevels - 1) {
      this.maxLevel = false;

      // Check if the navItem has sub-items and set the child items accordingly
      if (navItem.hasSubItems) {
        this.childItems = navItem.items;
        this.parentItem = navItem;
        this.mobileNavStack.push(navItem);
      }
    } else {
      if (navItem.hasSubItems && !this.maxLevel) {
        this.parentItem = navItem;
        const items = this.getSubItems(navItem, true);

        // Remove the first item (parent item) from the list
        items.shift();
        this.childItems = items;
        this.mobileNavStack.push(navItem);
        this.maxLevel = true;
      }
    }
  }

  // Get the sub-items of the specified item recursively
  getSubItems(item) {
    const items = [];

    function collectItems(currentItem) {
      items.push(currentItem);
      if (currentItem.hasSubItems && currentItem.items) {
        currentItem.items.forEach((subItem) => collectItems(subItem));
      }
    }

    collectItems(item);
    return items;
  }

  // Handle the back navigation in mobile view
  goBackUp() {
    this.mobileNavStack.pop();
    this.maxLevel = false;
    this.navImages = [];
    this.hasNavImages = false;

    if (this.mobileNavStack.length > 0) {
      this.parentItem = this.mobileNavStack[this.mobileNavStack.length - 1];
      this.childItems = this.parentItem.items;
    } else {
      this.childItems = [];
      this.parentItem = null;
    }
  }

  // Toggle the mobile navigation state
  toggleMobileNavigation() {
    this.open = !this.open;

    // Reset the mobile nav state
    this.parentItem = null;
    this.childItems = [];
    this.mobileNavStack = [];
    this.navImages = [];
    this.hasNavImages = false;
    this.maxLevel = false;
  }

  // Returns whether to show the image for the given item
  showImage(item) {
    const hasImage = item.mediaFile || item.customMediaFile;
    return item.useImage && (hasImage || item.usePlaceholderImage);
  }

  // Returns the image URL for the given item
  getImage(item, clientPath, imagePath) {
    if (item.customMediaFile) {
      return imagePath + clientPath + item.customMediaFile;
    } else if (item.mediaFile) {
      return imagePath + item.mediaFile;
    } else {
      return item.placeholderUrl ?? "";
    }
  }

  // Display the overlay when the dropdown is open
  showOverlay() {
    const overlay = document.querySelector(".content-overlay");
    if (overlay) {
      overlay.classList.add("show");
    }
  }

  // Hide the overlay when the dropdown is closed
  hideOverlay() {
    const overlay = document.querySelector(".content-overlay");
    if (overlay) {
      overlay.classList.remove("show");
    }
  }

  // Get the top bar navigation items based on the navbar ID or use secondary nav if specified
  getTopBarNavItems(navbarId, useSecondaryNav = false) {
    if (useSecondaryNav) {
      return this.secondaryNavItems;
    }
    return (
      this.additionalNavbars.find((bar) => bar.id == navbarId)?.items ?? []
    );
  }

  // Check if the mobile subheaders should be hidden based on the parent item and configuration
  hideSubHeader() {
    if(this.parentItem && this.hideMobileSubHeaders) {
      return !this.allowMobileSubHeadersList.includes(this.parentItem.name);
    } else {
      return this.hideMobileSubHeaders;
    }
  }

  // Check if the item should be shown as a navigation link based on its type and sub-items
  showNavLink(item, mobile = false) {
    if (
      (item.subItem == "group" || item.type == "group") &&
      !item.hasSubItems
    ) {
      return this.showGroups.includes(item.name);
    }

    if (mobile) {
      // Hide mobile subheaders if configured
      if (
        this.hideSubHeader() &&
        this.maxLevel &&
        item.hasSubItems &&
        item.type == "group"
      ) {
        return false;
      }

      // All other mobile cases
      return true;
    }

    return !item.hasSubItems && item.type !== "image";
  }

  // Check if the nav item has dropdown items (sub-items)
  hasDropdownItems(navItem) {
    // Check if the navItem has sub-items and if any of them are not of type 'image'
    return (
      navItem.items.length > 0 &&
      navItem.items.some((item) => item.type !== "image")
    );
  }

  // Filter out image types from the dropdown items
  getDropdownItems(navItem) {
    return navItem.items.filter((item) => item.type !== "image");
  }

  getMobileNavImages() {
    if(this.parentItem) {
      return this.parentItem.items.filter((item) => item.type === "image");
    }

    return [];
  }
}

/**
 * Class for managing condensed navigation items and their behavior.
 * Inherits from the base NavigationManager class.
 *
 * @class CondensedNavigationManager
 * @extends NavigationManager
 * @param {Array} navItems - The navigation items.
 * @param {Array} secondaryNavItems - The secondary navigation items.
 * @param {Array} additionalNavbars - The additional navigation items.
 * @param {number} maxMobileLevels - The maximum number of mobile levels.
 * @param {boolean} hideMobileSubHeaders - Whether to hide mobile subheaders.
 * @param {string} mobileFooterNav - The mobile footer navigation ID.
 * @param {Array} showGroups - The groups to show in the navigation regardless of subitems.
 * @param {boolean} showNavImagesOnMobile - Whether to show navigation images on mobile.
 * @param {boolean} useMobileShopNav - Whether to use mobile shop navigation.
 * @param {Array} allowMobileSubHeadersList - List of items that are allowed to show subheaders on mobile.
 * @param {boolean} showSecondaryNavOnMobile - Whether to show secondary navigation on mobile.
 * @param {boolean} useSecondaryNavInMobileFooter - Whether to use secondary navigation in the mobile footer.
 * @returns {CondensedNavigationManager} The condensed navigation manager instance.
 */
class CondensedNavigationManager extends NavigationManager {
  constructor(
    navItems,
    secondaryNavItems,
    additionalNavbars,
    maxMobileLevels,
    hideMobileSubHeaders,
    mobileFooterNav,
    showGroups,
    showNavImagesOnMobile,
    useMobileShopNav,
    allowMobileSubHeadersList,
    showSecondaryNavOnMobile,
    useSecondaryNavInMobileFooter
  ) {
    super(
      navItems,
      secondaryNavItems,
      additionalNavbars,
      maxMobileLevels,
      hideMobileSubHeaders,
      mobileFooterNav,
      showGroups,
      showNavImagesOnMobile,
      allowMobileSubHeadersList,
      showSecondaryNavOnMobile,
      useSecondaryNavInMobileFooter
    );
    this.rightClick = false;
    this.showSideNav = false;
    this.maxNavWidth = 1100;
    this.navPadding = 20;
    this.dropdownWidth = 160;
    this.useMobileShopNav = useMobileShopNav;
  }

  // Find the category parent item and return its items if it exists
  getCategoryNavItems() {
    return (
      this.navItems.find((item) => item.categoryParent === true)?.items ?? []
    );
  }

  // Set the mobile navigation items based on the configuration and secondary nav items
  setMobileNavItems() {
    if (this.useMobileShopNav) {
      if(this.showSecondaryNavOnMobile) {
        this.mobileNavItems = [...this.navItems, ...this.secondaryNavItems];
      } else {
        this.mobileNavItems = this.navItems;
      }
    } else {
      if(this.showSecondaryNavOnMobile) {
        this.mobileNavItems = [
          ...this.navItems.find((item) => item.categoryParent === true).items,
          ...this.navItems.filter((item) => item.categoryParent !== true),
          ...this.secondaryNavItems,
        ];
      } else {
        this.mobileNavItems = [
          ...this.navItems.find((item) => item.categoryParent === true).items,
          ...this.navItems.filter((item) => item.categoryParent !== true)
        ];
      }
    }
  }

  // Use child items if available, otherwise use mobile nav items
  getMobileNavItems() {
    if (this.childItems.length > 0) {
      return this.childItems.filter((item) => item.type !== "image");
    }
    return this.mobileNavItems.filter((item) => item.type !== "image");
  }

  // Calculates the width of the side navigation based on the main nav link and the dropdown wrapper
  calculateSideNavWidth(className, direction) {
    let mainNavLink;
    if (direction === "right") {
      mainNavLink = document.querySelector(
        ".category-nav .main-nav-link:last-of-type"
      );
    } else {
      mainNavLink = document.querySelector(".category-nav .main-nav-link");
    }
    const wrapper = document.querySelector(`.dropdown-wrapper.${className}`);
    const localDropdown = wrapper?.querySelector(".local-dropdown") ?? null;
    const sideNav = wrapper?.querySelector(`.side-navigation-wrapper`) ?? null;

    if (mainNavLink && localDropdown) {
      // Set the initial width of the dropdown
      const originalDropdownWidth = localDropdown.getBoundingClientRect().width;
      wrapper.classList.add("expanded");

      // Calculate and set the final width after the grid expands
      requestAnimationFrame(() => {
        let right, left, width, availableSpace;
        // Calculate the available space based on the direction
        if (direction === "right") {
          right = mainNavLink.getBoundingClientRect().right;
          left = localDropdown.getBoundingClientRect().left;
          availableSpace = Math.min(
            window.innerWidth - left - this.navPadding,
            this.maxNavWidth
          );
        } else {
          right = localDropdown.getBoundingClientRect().right;
          left = mainNavLink.getBoundingClientRect().left;
          availableSpace = Math.min(right - this.navPadding, this.maxNavWidth);
        }

        // Set the width of the dropdown and the wrapper
        width = Math.max(right - left, availableSpace);
        wrapper.style.width = `${width}px`;
        localDropdown.style.width = `${originalDropdownWidth}px`;

        setTimeout(() => {
          // Show the side nav after the width is set
          sideNav.classList.add("show");
        }, 1);
      });
    }
  }

  // Resets the side navigation by hiding it and resetting its width
  resetSideNav(className) {
    this.showSideNav = false;
    this.currentNavItem = null;
    this.hideOverlay();

    const wrapper = document.querySelector(`.dropdown-wrapper.${className}`);
    const sideNav = wrapper?.querySelector(`.side-navigation-wrapper`) ?? null;
    const localDropdown = document.querySelector(".local-dropdown");

    if (wrapper) {
      // Reset the width of the dropdown and the wrapper
      wrapper.classList.remove("expanded");
      const linkWidth = wrapper.parentElement.getBoundingClientRect().width;
      wrapper.style.width = linkWidth + "px";
      localDropdown.style.width = linkWidth + "px";
    }
    if (sideNav) {
      sideNav.classList.remove("show");
    }
  }

  // Opens the side navigation for the specified nav item and calculates its width based on the direction
  openSideNav(navItem, className, direction = "right") {
    if (navItem.hasSubItems) {
      this.currentNavItem = navItem;
      this.setNavImages(navItem);

      if (!this.showSideNav) {
        this.showSideNav = true;
        this.showOverlay();
        this.calculateSideNavWidth(className, direction);
      }
    } else {
      this.resetNavImages();
      this.resetSideNav(className);
    }
  }

  // Toggles the dropdown for the specified nav item
  openDropdown(navItem) {
    this.currentNavItem = navItem;
    this.showDropdown = true;
    this.showOverlay();

    const nav = document.querySelector(".desktop-browse-nav");
    nav.addEventListener("mouseleave", (e) => {
      this.closeDropdown();
    });

    document.addEventListener("click", (e) => {
      if (e.target.classList.contains("desktop-browse-nav")) {
        this.closeDropdown();
      }
    });

    const dropdown = document.querySelector(".main-nav-dropdown");
    if (dropdown) {
      // This allows for right click to open the dropdown and keep it open
      dropdown.addEventListener("contextmenu", (event) => {
        this.rightClick = true;
        event.stopPropagation(); // Stop the event from propagating

        setInterval(() => {
          this.rightClick = false;
        }, 2000);
      });
    }
  }

  // Toggles the dropdown for the specified nav item
  toggleDropdown(navItem) {
    if (this.showDropdown && this.currentNavItem.id === navItem.id) {
      this.closeDropdown();
    } else {
      this.openDropdown(navItem);
    }
  }

  // Closes the dropdown and resets the current nav item and hides the overlay
  closeDropdown() {
    if (!this.rightClick) {
      this.showDropdown = false;
      this.currentNavItem = [];
      this.hideOverlay();
    }
  }

  // Set the images to display in the navigation based on the nav item
  setNavImages(navItem) {
    this.navImages = navItem.items.filter((item) => item.type == "image");
    this.hasNavImages = this.navImages.length > 0;
  }

  // Resets the navigation images and sets the hasNavImages flag to false
  resetNavImages() {
    this.navImages = [];
    this.hasNavImages = false;
  }

  // Retyrns the sub items of the specified item in the browse navigation type
  getBrowseSubNavItems(subItem) {
    return subItem.items.filter(
      (item) =>
        item.type === "button" ||
        item.type === "item" ||
      (item.type === "group" && item.items.length > 0)
    );
  }

  // Whether the nav item has images or not
  navItemHasImages(navItem) {
    return navItem.items.some((item) => item.type === "image");
  }
}

/**
 * Class for managing standard navigation items and their behavior.
 * Inherits from the base NavigationManager class.
 *
 * @class StandardNavigationManager
 * @extends NavigationManager
 * @param {Array} navItems - The navigation items.
 * @param {Array} secondaryNavItems - The secondary navigation items.
 * @param {Array} additionalNavbars - The additional navigation items.
 * @param {number} maxMobileLevels - The maximum number of mobile levels.
 * @param {boolean} hideMobileSubHeaders - Whether to hide mobile subheaders.
 * @param {boolean} useMobileShopNav - Whether to use mobile shop navigation.
 * @param {string} mobileFooterNav - The mobile footer navigation ID.
 * @param {Array} showGroups - The groups to show in the navigation regardless of subitems.
 * @param {boolean} showNavImagesOnMobile - Whether to show navigation images on mobile.
 * @param {Array} allowMobileSubHeadersList - List of items that are allowed to show subheaders on mobile.
 * @param {boolean} showSecondaryNavOnMobile - Whether to show secondary navigation on mobile.
 * @param {boolean} useSecondaryNavInMobileFooter - Whether to use secondary navigation in the mobile footer.
 * @returns {StandardNavigationManager} The standard navigation manager instance.
 */
class StandardNavigationManager extends NavigationManager {
  constructor(
    navItems,
    secondaryNavItems,
    additionalNavbars,
    maxMobileLevels,
    hideMobileSubHeaders,
    useMobileShopNav,
    mobileFooterNav,
    showGroups,
    showNavImagesOnMobile,
    allowMobileSubHeadersList = [],
    showSecondaryNavOnMobile,
    useSecondaryNavInMobileFooter
  ) {
    super(
      navItems,
      secondaryNavItems,
      additionalNavbars,
      maxMobileLevels,
      hideMobileSubHeaders,
      mobileFooterNav,
      showGroups,
      showNavImagesOnMobile,
      allowMobileSubHeadersList,
      showSecondaryNavOnMobile,
      useSecondaryNavInMobileFooter
    );
    this.subNavItems = [];
    this.useMobileShopNav = useMobileShopNav;
  }

  // Sets up the mobile navigation items based on the configuration and secondary nav items
  setMobileNavItems() {
    if (this.secondaryNavItems.length > 0 && this.showSecondaryNavOnMobile) {
      if (this.useMobileShopNav) {
        const shopNav = {
          name: "Shop",
          url: "/",
          type: "nav",
          items: [...this.navItems],
          hasSubItems: true,
        };
        this.mobileNavItems = [shopNav, ...this.secondaryNavItems];
      } else {
        this.mobileNavItems = [...this.navItems, ...this.secondaryNavItems];
      }
    } else {
      this.mobileNavItems = [...this.navItems];
    }
  }

  // Opens the dropdown for the specified nav item and sets the subnav items
  openDropdown(navItem = null) {
    if (navItem.items.length > 0) {
      this.showDropdown = true;
      this.setNavSubItems(navItem);
      this.showOverlay();
    } else {
      this.showDropdown = false;
      this.hideOverlay();
    }

    document
      .querySelectorAll(".main-nav-dropdown .inner-dropdown")
      .forEach((dropdown) => {
        dropdown.addEventListener("contextmenu", (event) => {
          this.rightClick = true;
          event.stopPropagation(); // Stop the event from propagating

          setInterval(() => {
            this.rightClick = false;
          }, 2000);
        });
      });
  }

  // CLoses the dropdown and resets the subnav items and nav images
  closeDropdown() {
    if (!this.rightClick) {
      this.subNavItems = [];
      this.navImages = [];
      this.hasNavImages = false;
      this.showDropdown = false;
      this.hideOverlay();
    }
  }

  // Sets the navigation images based on the subnav items
  setNavImages() {
    if (this.subNavItems.length > 0) {
      this.navImages = this.subNavItems.filter((item) => item.type == "image");
      this.hasNavImages = this.navImages.length > 0;
    }
  }

  // Sets the subnav items and filters out images, also sets the nav images
  setNavSubItems(navItem) {
    this.subNavItems = [];
    this.navImages = [];
    this.hasNavImages = false;

    for (const item of navItem.items) {
      if (item.type === "image") {
        this.navImages.push(item);
        this.hasNavImages = true;
      } else {
        this.subNavItems.push(item);
      }
    }
  }
}


/**
 * @function navigationManager
 * @description Manages the state and behavior of the header navigation system used in header.php.
 * This function creates and returns either a standard or condensed navigation manager
 * based on the specified navigation type.
 *
 * @param {Object} options - Configuration options for the navigation
 * @param {Object} options.navItemsJson - JSON data for primary navigation items
 * @param {Object|null} [options.secondaryNavItemsJson=null] - JSON data for secondary navigation items
 * @param {Object|null} [options.additionalNavbarsJson=null] - JSON data for any additional navigation bars
 * @param {string} [options.navType="standard"] - Navigation type, either "standard" or "condensed"
 * @param {number} [options.maxMobileLevels=2] - Maximum levels to show in mobile navigation
 * @param {boolean} [options.hideMobileSubHeaders=true] - Whether to hide sub-headers in mobile view
 * @param {boolean} [options.useMobileShopNav=false] - Whether to use specialized shop navigation on mobile
 * @param {number|null} [options.mobileFooterNav=null] - ID for mobile footer navigation or null
 * @param {string[]} [options.showGroups=["Fireplaces", "Massage Chairs"]] - Groups to be displayed in navigation
 * @param {boolean} [options.showNavImagesOnMobile=true] - Whether to display navigation images on mobile
 * @param {string[]} [options.allowMobileSubHeadersList=[]] - List of sub-headers allowed on mobile
 * @param {boolean} [options.showSecondaryNavOnMobile=false] - Whether to show secondary nav on mobile
 * @param {boolean} [options.useSecondaryNavInMobileFooter=true] - Whether to include secondary nav in mobile footer
 *
 * @returns {StandardNavigationManager|CondensedNavigationManager} The appropriate navigation manager instance
 */
function navigationManager({
  navItemsJson,
  secondaryNavItemsJson = null,
  additionalNavbarsJson = null,
  navType = "standard",
  maxMobileLevels = 2,
  hideMobileSubHeaders = true,
  useMobileShopNav = false,
  mobileFooterNav = null,
  showGroups = ["Fireplaces", "Massage Chairs"],
  showNavImagesOnMobile = true,
  allowMobileSubHeadersList = [],
  showSecondaryNavOnMobile = false,
  useSecondaryNavInMobileFooter = true,
}) {

  // Parse the JSON strings for navigation items and additional navbars
  const navItems = setNavItems(navItemsJson);
  const secondaryNavItems = secondaryNavItemsJson
    ? setNavItems(secondaryNavItemsJson)
    : [];
  const additionalNavbars = additionalNavbarsJson
    ? getAdditionalNavBars(additionalNavbarsJson)
    : [];

  // Check if the navType is "condensed" and return the appropriate manager
  if (navType === "condensed") {
    return new CondensedNavigationManager(
      navItems,
      secondaryNavItems,
      additionalNavbars,
      maxMobileLevels,
      hideMobileSubHeaders,
      mobileFooterNav,
      showGroups,
      showNavImagesOnMobile,
      useMobileShopNav,
      allowMobileSubHeadersList,
      showSecondaryNavOnMobile,
      useSecondaryNavInMobileFooter
    );
  } else {
    return new StandardNavigationManager(
      navItems,
      secondaryNavItems,
      additionalNavbars,
      maxMobileLevels,
      hideMobileSubHeaders,
      useMobileShopNav,
      mobileFooterNav,
      showGroups,
      showNavImagesOnMobile,
      allowMobileSubHeadersList,
      showSecondaryNavOnMobile,
      useSecondaryNavInMobileFooter
    );
  }
}

/**
 * @function categoryPageManager
 * @description Manages the state and behavior of the category page navigation.
 *
 * @param {Object} options - Configuration options for the navigation
 * @param {Object} options.navItemsJson - JSON data for primary navigation items
 *
 * @returns {StandardNavigationManager|CondensedNavigationManager} The appropriate navigation manager instance
 */
function categoryPageManager({navItemsJson}) {

  // Parse the JSON strings for navigation items
  const navItems = setNavItems(navItemsJson, false, true);

  return {
    navItems,

    getId(item) {
      if(item.categoryId) {
        return `category-${item.categoryId}`
      }
      return 'category-0'
    },

    // Returns the image URL for the given item
    getImage(item, clientPath, imagePath) {
      if (item.customMediaFile) {
        return imagePath + clientPath + item.customMediaFile;
      } else if (item.mediaFile) {
        return imagePath + item.mediaFile;
      } else {
        return "https://cdn.knorrweb.com/no-furniture-image.webp";
      }
    },

    // Check if item is a group with items
    isGroup(item) {
      return item.type === 'group' && item.items && item.items.length > 0;
    },

    // Check if item is a direct item (not a group)
    isDirectItem(item) {
      return item.type !== 'group';
    },

    // Check if item is a brand
    isBrand(item) {
      return item.brandId != null;
    }
  }
}

/**
 * Progressively enhances the server-rendered mobile footer navigation with
 * animated, independently controlled link drawers.
 *
 * @returns {Object} Alpine component state and behavior for the mobile footer.
 */
function footerNavigationManager() {
  return {
    expandedSectionId: null,

    toggleFooterSection(sectionId) {
      this.expandedSectionId =
        this.expandedSectionId === sectionId ? null : sectionId;
    },

    isFooterSectionOpen(sectionId) {
      return this.expandedSectionId === sectionId;
    },
  };
}

/**
 * @function searchSuggestions
 * Returns an object for managing search suggestions for the header search form to be used in header.php.
 *
 * @returns {Object} An object containing methods and properties for managing search suggestions.
 * @property {string} inputValue - The current value of the search input.
 * @property {boolean} showSuggestions - Whether to show the suggestions dropdown.
 * @property {Array} categorySuggestions - Array of category suggestions.
 * @property {Array} productSuggestions - Array of product suggestions.
 * @property {Array} brandSuggestions - Array of brand suggestions.
 * @property {boolean} showSearchBar - Whether to show the search bar.
 *
 * @method init - Initializes the search suggestions functionality.
 * @method clearSuggestions - Clears the suggestions and resets the input value.
 * @method handleOnChange - Handles input change and updates suggestions.
 * @method updateSuggestions - Updates suggestions based on input value.
 * @method getSuggestions - Fetches suggestions from the server and updates the suggestions arrays.
 * @method getResultsArray - Gets the suggestions from the response object.
 * @method searchSuggestion - Handles search suggestion click and redirects to the search results page.
 *
 */
function searchSuggestions() {
  return {
    inputValue: "",
    showSuggestions: false,
    categorySuggestions: [],
    productSuggestions: [],
    brandSuggestions: [],
    showSearchBar: false,

    init() {
      const body = document.querySelector("body");
      const searchForm = document.querySelector("form.searchbar");
      const searchInput = searchForm.querySelector('input[type="text"]');

      // Close suggestions and clear search when clicking outside the search form
      body.addEventListener("click", (e) => {
        if (searchForm.contains(e.target)) return;
        this.clearSuggestions(searchInput);
      });

      searchInput.addEventListener("keydown", (e) => {
        if (e.key === "Escape") {
          this.clearSuggestions(searchInput);
        }
      });
    },

    // Clear suggestions and reset input value
    clearSuggestions(searchInput) {
      this.showSuggestions = false;
      this.inputValue = "";
      this.categorySuggestions = [];
      this.productSuggestions = [];
      this.brandSuggestions = [];
      searchInput.value = "";
    },

    // Handle input change and update suggestions
    handleOnChange(value) {
      this.inputValue = value;
      this.updateSuggestions();
    },

    // Update suggestions based on input value
    updateSuggestions() {
      if (this.inputValue.length > 0) {
        this.showSuggestions = true;
        this.getSuggestions();
      } else {
        this.showSuggestions = false;
      }
    },

    // Fetch suggestions from the server and update the suggestions arrays
    getSuggestions() {
      fetch(globalObject.url + "ajax/search.php?action=suggest", {
        method: "POST",
        body: new URLSearchParams({ q: this.inputValue }),
      })
        .then((response) => response.json())
        .then((response) => {
          if (response && response.suggest) {
            this.categorySuggestions = [
              ...this.getResultsArray(response.suggest.categorySuggest),
            ];
            this.productSuggestions = [
              ...this.getResultsArray(response.suggest.productSuggest),
            ];
            this.brandSuggestions = [
              ...this.getResultsArray(response.suggest.brandTitle),
            ];
          }
        })
        .catch((error) => console.error("Error:", error));
    },

    // Get the suggestions from the response object
    getResultsArray(data) {
      const resultsData = Object.values(data)[0];
      let result = [];
      if (resultsData && resultsData.numFound > 0) {
        result = resultsData.suggestions.map((result) => {
          let div = document.createElement("div");
          div.innerHTML = result.term.replace(/<\/?b>/g, "");
          return div.textContent;
        });
      }

      return result;
    },

    // Handle search suggestion click and redirect to the search results page
    searchSuggestion(suggestion, field) {
      fetch(globalObject.url + "ajax/search.php?action=suggestionUrl", {
        method: "POST",
        body: new URLSearchParams({ q: suggestion, f: field }),
      })
        .then((response) => response.text())
        .then((response) => {
          if (response.length > 0) {
            window.location.href = response;
          }
        })
        .catch((error) => console.error("Error:", error));
    },
  };
}


/**
 * Initializes the category swiper for the category navigation.
 */
const initCategorySwiper = () => {
  const categorySwiper = document.querySelector(".swiper-category");
  if (!categorySwiper) return;

  const container = document.querySelector(".category-swiper-container");
  let hasInteracted = false;

  const swiper = new Swiper(".swiper-category", {
    direction: "horizontal",
    loop: false,
    spaceBetween: 10,
    slidesPerView: "auto",
    on: {
      slideChange: function() {
        if (!hasInteracted && container) {
          container.classList.add('hint-hidden');
          hasInteracted = true;
        }
      },
      touchStart: function() {
        if (!hasInteracted && container) {
          container.classList.add('hint-hidden');
          hasInteracted = true;
        }
      },
      sliderMove: function() {
        if (!hasInteracted && container) {
          container.classList.add('hint-hidden');
          hasInteracted = true;
        }
      },
      touchMove: function() {
        if (!hasInteracted && container) {
          container.classList.add('hint-hidden');
          hasInteracted = true;
        }
      }
    }
  });

  // Also hide on scroll/drag for desktop
  if (categorySwiper && container) {
    categorySwiper.addEventListener('mousedown', function() {
      if (!hasInteracted) {
        container.classList.add('hint-hidden');
        hasInteracted = true;
      }
    });
  }
};

document.addEventListener("DOMContentLoaded", () => {
  initCategorySwiper();
});
